import { Dialog, Menu, RadioGroup, Transition } from "@headlessui/react";
import {
  CheckIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CloudArrowDownIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import { shortDateFormat } from "../../common/utils/dateformat";
import {
  AgentEnterpriseAgreementMode,
  AgentQuery,
  useEnterpriseAgreementsQuery,
  useUpdateEnterpriseAgreementForAgentMutation,
  useAuthorizeAgentMutation,
  useDeauthorizeAgentMutation,
  useEnableAgentMutation,
  useDisableAgentMutation,
  useUpdateAuthDocForAgentMutation,
  useUpdateExternalCommunicationForAgentMutation,
  useUpdateListingQueryPermissionForAgentMutation,
  InheritMode,
  ExternalCommunicationState,
  ListingQueryPermissionValue,
  useCreateIngestTaskForPrimaryKeyMutation,
  IngestTaskTarget,
} from "../../graphql/generated";
import ExternalCommunicationDialog from "./external-communication-dialog";
import ListingQueryPermissionDialog from "./listing-query-permission-dialog";
import { useNavigate } from "react-router-dom";

const AgentItemActions: React.FC<{ agent?: AgentQuery["agent"] }> = ({
  agent,
}) => {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<string>(
    agent?.enterpriseAgreement?.id ?? ""
  );
  const [docOpen, setDocOpen] = React.useState(false);
  const [doc, setDoc] = React.useState(agent?.authorizationDocument);
  const { notifier } = React.useContext(NotificationContext);

  const [externalCommunicationOpen, setExternalCommunicationOpen] =
    React.useState(false);

  const [listingQueryPermissionOpen, setListingQueryPermissionOpen] =
    React.useState(false);

  const [{ data }] = useEnterpriseAgreementsQuery({
    variables: {},
  });

  const [, mutation] = useUpdateEnterpriseAgreementForAgentMutation();

  const inheritAgreement = React.useCallback(async () => {
    await mutation({
      agentId: agent?.id ?? "",
      mode: AgentEnterpriseAgreementMode.Auto,
    }).then(notifier.notifyGraphql("Agent inheriting enterprise agreement."));
    setOpen(false);
  }, [agent?.id, mutation]);

  const excludeAgreement = React.useCallback(async () => {
    await mutation({
      agentId: agent?.id ?? "",
      mode: AgentEnterpriseAgreementMode.ManualNone,
    }).then(
      notifier.notifyGraphql("Agent excluded from enterprise agreement.")
    );
  }, [agent?.id, mutation]);

  const linkAgreement = React.useCallback(async () => {
    await mutation({
      agentId: agent?.id ?? "",
      mode: AgentEnterpriseAgreementMode.Manual,
      enterpriseAgreementId: selected,
    }).then(notifier.notifyGraphql("Agent linked to enterprise agreement."));
  }, [agent?.id, mutation, selected]);

  const [, authMutation] = useAuthorizeAgentMutation();

  const authorizeAgent = React.useCallback(async () => {
    await authMutation({
      id: agent?.id ?? "",
    }).then(notifier.notifyGraphql("Agent authorized."));
  }, [agent?.id, authMutation]);

  const [, deauthMutation] = useDeauthorizeAgentMutation();

  const deauthorizeAgent = React.useCallback(async () => {
    await deauthMutation({
      id: agent?.id ?? "",
    }).then(notifier.notifyGraphql("Agent deauthorized."));
  }, [agent?.id, deauthMutation]);

  const [, enableMutation] = useEnableAgentMutation();

  const enableAgent = React.useCallback(async () => {
    await enableMutation({
      id: agent?.id ?? "",
    }).then(notifier.notifyGraphql("Agent enabled."));
  }, [agent?.id, enableMutation]);

  const [, disableMutation] = useDisableAgentMutation();

  const disableAgent = React.useCallback(async () => {
    await disableMutation({
      id: agent?.id ?? "",
    }).then(notifier.notifyGraphql("Agent disabled."));
  }, [agent?.id, disableMutation]);

  const [, updateDocMutation] = useUpdateAuthDocForAgentMutation();

  const updateDoc = React.useCallback(async () => {
    if (doc !== agent?.authorizationDocument) {
      await updateDocMutation({
        id: agent?.id ?? "",
        newDoc: doc ?? "",
      }).then(notifier.notifyGraphql("Authorization Document Updated"));
      setDocOpen(false);
    }
  }, [agent?.id, updateDocMutation, agent?.authorizationDocument, doc]);

  const [, updateExternalCommunicationForAgentMutation] =
    useUpdateExternalCommunicationForAgentMutation();

  const updateExternalCommunication = React.useCallback(
    async (mode: InheritMode, value?: ExternalCommunicationState) => {
      await updateExternalCommunicationForAgentMutation({
        agentId: agent?.id ?? "",
        mode,
        value,
      }).then(notifier.notifyGraphql("External communication mode updated."));
      setExternalCommunicationOpen(false);
    },
    [
      agent?.id,
      updateExternalCommunicationForAgentMutation,
      setExternalCommunicationOpen,
    ]
  );

  const [, updateListingQueryPermissionForAgentMutation] =
    useUpdateListingQueryPermissionForAgentMutation();

  const updateListingQueryPermission = React.useCallback(
    async (mode: InheritMode, value?: ListingQueryPermissionValue) => {
      await updateListingQueryPermissionForAgentMutation({
        agentId: agent?.id ?? "",
        mode,
        value,
      }).then(notifier.notifyGraphql("Listing query permission updated."));
      setListingQueryPermissionOpen(false);
    },
    [
      agent?.id,
      updateListingQueryPermissionForAgentMutation,
      setListingQueryPermissionOpen,
    ]
  );

  const [
    { fetching: createIngestTaskForPrimaryKeyFetching },
    createIngestTaskForPrimaryKeyMutation,
  ] = useCreateIngestTaskForPrimaryKeyMutation();
  const navigate = useNavigate();
  const createIngestTaskForPrimaryKey = React.useCallback(async () => {
    if (createIngestTaskForPrimaryKeyFetching) {
      return;
    }
    const { data, error } = await createIngestTaskForPrimaryKeyMutation({
      id: agent?.id ?? "",
      mlsId: agent?.mls?.id ?? "",
      target: IngestTaskTarget.Agent,
    }).then(notifier.notifyGraphql(`Created ingest task for ${agent?.name}`));
    if (!error) {
      navigate(`/ingest_tasks/${data?.createIngestTaskForPrimaryKey.id}`);
    }
  }, [
    createIngestTaskForPrimaryKeyFetching,
    createIngestTaskForPrimaryKeyMutation,
    navigate,
    agent,
  ]);

  return (
    <>
      <Menu as="div" className="relative inline-block text-left mt-4 sm:mt-0">
        <div>
          <Menu.Button className="bg-gray-100 rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={() => setOpen(true)}
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                  >
                    <PencilIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Update Enterprise Agreement
                  </div>
                )}
              </Menu.Item>
              {agent?.enabled ? (
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={disableAgent}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "group flex items-center px-4 py-2 text-sm cursor-pointer"
                      )}
                    >
                      <PencilIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Disable Signup
                    </div>
                  )}
                </Menu.Item>
              ) : (
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={enableAgent}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "group flex items-center px-4 py-2 text-sm cursor-pointer"
                      )}
                    >
                      <PencilIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Enable Signup
                    </div>
                  )}
                </Menu.Item>
              )}
              {agent?.authorized ? (
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={deauthorizeAgent}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "group flex items-center px-4 py-2 text-sm cursor-pointer"
                      )}
                    >
                      <PencilIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Deauthorize Agent
                    </div>
                  )}
                </Menu.Item>
              ) : (
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={authorizeAgent}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "group flex items-center px-4 py-2 text-sm cursor-pointer"
                      )}
                    >
                      <PencilIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Authorize Agent
                    </div>
                  )}
                </Menu.Item>
              )}
              <Menu.Item>
                {({ active }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() => setDocOpen(!docOpen)}
                  >
                    <PencilIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Update Authorization Document
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() => setExternalCommunicationOpen(true)}
                  >
                    <EnvelopeIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Update external communication
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={() => setListingQueryPermissionOpen(true)}
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                  >
                    <MagnifyingGlassIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Update listing query permission
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={createIngestTaskForPrimaryKey}
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                  >
                    <CloudArrowDownIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Re-ingest Agent from MLS
                  </div>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      <ExternalCommunicationDialog
        isOpen={externalCommunicationOpen}
        onClose={() => setExternalCommunicationOpen(false)}
        onAuto={() => updateExternalCommunication(InheritMode.Auto)}
        onManual={(value: ExternalCommunicationState) =>
          updateExternalCommunication(InheritMode.Manual, value)
        }
      />

      <ListingQueryPermissionDialog
        isOpen={listingQueryPermissionOpen}
        onClose={() => setListingQueryPermissionOpen(false)}
        onAuto={() => updateListingQueryPermission(InheritMode.Auto)}
        onManual={(value: ListingQueryPermissionValue) =>
          updateListingQueryPermission(InheritMode.Manual, value)
        }
      />

      <Transition.Root show={open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 overflow-hidden z-50"
          onClose={setOpen}
        >
          <div className="absolute inset-0 overflow-hidden h-screen">
            <Transition.Child
              as={React.Fragment}
              enter="ease-in-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="w-screen max-w-2xl">
                  <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-none">
                    <div className="px-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          Enterprise Agreement
                        </Dialog.Title>
                        <div className="ml-3 h-7 flex items-center">
                          <button
                            type="button"
                            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onClick={() => setOpen(false)}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 relative flex-1  flex flex-col overflow-scroll">
                      <div className="px-4 sm:px-6 py-4 sm:py-6 mt-4 grow overflow-scroll">
                        <RadioGroup value={selected} onChange={setSelected}>
                          <RadioGroup.Label className="sr-only">
                            Agent
                          </RadioGroup.Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {data?.enterpriseAgreements.map((agreement) => (
                              <RadioGroup.Option
                                key={agreement?.id}
                                value={agreement?.id}
                                className={({ checked, active }) =>
                                  classNames(
                                    checked
                                      ? "border-transparent"
                                      : "border-gray-300",
                                    active ? "ring-2 ring-indigo-500" : "",
                                    "relative block bg-white border rounded-lg shadow-sm px-6 py-4 cursor-pointer sm:flex sm:justify-between focus:outline-none"
                                  )
                                }
                              >
                                {({ active, checked }) => (
                                  <>
                                    <div className="flex items-center">
                                      <div className="text-sm">
                                        <RadioGroup.Label
                                          as="div"
                                          className="font-medium text-gray-900"
                                        >
                                          {agreement?.name}{" "}
                                        </RadioGroup.Label>
                                        <RadioGroup.Description
                                          as="div"
                                          className="text-gray-500"
                                        >
                                          <p className="sm:inline">
                                            {shortDateFormat(
                                              agreement?.createdAt
                                            )}
                                          </p>{" "}
                                          <span
                                            className="hidden sm:inline sm:mx-1"
                                            aria-hidden="true"
                                          >
                                            &middot;
                                          </span>{" "}
                                          <p className="sm:inline">
                                            {agreement?.agentStripePriceId}
                                          </p>
                                        </RadioGroup.Description>
                                      </div>
                                    </div>
                                    <div
                                      className={classNames(
                                        active ? "border" : "border-2",
                                        checked
                                          ? "border-indigo-500"
                                          : "border-transparent",
                                        "absolute -inset-px rounded-lg pointer-events-none"
                                      )}
                                      aria-hidden="true"
                                    />
                                  </>
                                )}
                              </RadioGroup.Option>
                            ))}
                          </div>
                        </RadioGroup>
                      </div>
                      <div className="pt-5 px-4 sm:px-6">
                        <div className="flex justify-end">
                          <button
                            onClick={() => setOpen(false)}
                            type="button"
                            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                          {agent?.enterpriseAgreementMode !==
                            AgentEnterpriseAgreementMode.Auto && (
                            <button
                              onClick={inheritAgreement}
                              type="submit"
                              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              Inherit From Office
                            </button>
                          )}
                          {agent?.enterpriseAgreementMode !==
                            AgentEnterpriseAgreementMode.ManualNone && (
                            <button
                              onClick={excludeAgreement}
                              type="submit"
                              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Exclude From Office Agreement
                            </button>
                          )}
                          <button
                            onClick={linkAgreement}
                            disabled={!selected}
                            type="submit"
                            className={classNames(
                              selected
                                ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                                : "bg-gray-600 hover:bg-gray-700 focus:ring-gray-500",
                              "ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            )}
                          >
                            Link Selected
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={docOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          onClose={setDocOpen}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                <div>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <CheckIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      Update Authorization Document
                    </Dialog.Title>
                    <div className="mt-2">
                      <div className="mt-1">
                        <input
                          type="url"
                          name="slug"
                          id="slug"
                          value={doc ?? ""}
                          onChange={(e) => setDoc(e.target.value)}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          placeholder="Link To Authorization Document"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    onClick={updateDoc}
                  >
                    Update Authorization Document
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default AgentItemActions;
